/**
 * RegisterForm — client component for user registration
 *
 * Posts to /api/register then auto-signs in via NextAuth.
 */

"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function RegisterForm() {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        setIsLoading(true);

        const formData = new FormData(e.currentTarget);
        const name = formData.get("name") as string;
        const email = formData.get("email") as string;
        const password = formData.get("password") as string;
        const confirmPassword = formData.get("confirmPassword") as string;
        const referralCode = formData.get("referralCode") as string;

        if (password !== confirmPassword) {
            toast.error("Les mots de passe ne correspondent pas");
            setIsLoading(false);
            return;
        }

        try {
            const res = await fetch("/api/register", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, password, confirmPassword, referralCode }),
            });

            if (!res.ok) {
                const data = await res.json();
                toast.error(data.error ?? "Erreur lors de l'inscription");
                setIsLoading(false); // Reset loading on error
                return;
            }

            // Auto sign-in after registration
            const result = await signIn("credentials", {
                email,
                password,
                redirect: false,
            });

            if (result?.error) {
                toast.success("Compte créé ! Connectez-vous.");
                router.push("/auth/login");
                return;
            }

            toast.success("Compte créé et connecté !");
            router.push("/");
            router.refresh();
        } catch {
            toast.error("Erreur réseau");
            setIsLoading(false); // Reset loading
        }
    }

    const handleGoogleLogin = () => {
        setIsLoading(true);
        signIn("google", { callbackUrl: "/" });
    };

    return (
        <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                    <Label htmlFor="name">Nom complet</Label>
                    <Input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        disabled={isLoading}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="votre@email.fr"
                        autoComplete="email"
                        required
                        disabled={isLoading}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="password">Mot de passe</Label>
                    <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        required
                        disabled={isLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                        Min. 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre
                    </p>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        disabled={isLoading}
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="referralCode">Code de parrainage (Optionnel)</Label>
                    <Input
                        id="referralCode"
                        name="referralCode"
                        type="text"
                        placeholder="Ex: HP-A1B2C3"
                        disabled={isLoading}
                        className="bg-hp-teal/5 border-hp-teal/20"
                    />
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp hover:from-hp-purple-dark hover:to-hp-orange-dark hover:shadow-hp-hover rounded-xl" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                            Création...
                        </>
                    ) : (
                        "Créer mon compte"
                    )}
                </Button>
            </form>

            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Ou</span>
                </div>
            </div>

            <Button
                variant="outline"
                type="button"
                className="w-full rounded-xl border-hp-gray/20 hover:bg-gray-50 dark:hover:bg-white/5"
                disabled={isLoading}
                onClick={handleGoogleLogin}
            >
                {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                    <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
                        <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                    </svg>
                )}
                S'inscrire avec Google
            </Button>
        </div>
    );
}
